import {
  computeOneCartItemTotalDetails,
  MinimalCartItem,
  Listing,
  IncludedTax,
  NonIncludedTax,
  round,
} from './computeCartTotalDetails'
import {
  hasCompoundTaxFeatures,
  applyCompoundTaxesToAmount,
  type Tax,
} from './compoundTaxHelpers'

type CartItemCalculation = ReturnType<typeof computeOneCartItemTotalDetails>

export const computeCartItemWithCompoundTaxes = (
  cartItem: MinimalCartItem,
  listing: Listing,
  percentageDiscountByCode?: number,
  fixedPercentageDiscount?: number,
  fixedAmountDiscount?: number,
  isRequestToBookEnabled?: boolean,
  fixedExperienceAmountDiscount?: number,
): CartItemCalculation => {
  // STEP 1: Get base calculation (handles discounts, add-ons, etc.)
  const baseCalculation = computeOneCartItemTotalDetails(
    cartItem,
    listing,
    percentageDiscountByCode,
    fixedPercentageDiscount,
    fixedAmountDiscount,
    isRequestToBookEnabled,
    fixedExperienceAmountDiscount,
  )

  // STEP 2: Check if we need compound tax calculation
  const listingTaxes = (() => {
    if (cartItem.groupId && listing.resourceGroups) {
      const resourceGroup = listing.resourceGroups.find(
        group => group.id === cartItem.groupId,
      )
      return resourceGroup?.taxes || []
    }
    return listing.taxes || []
  })() as Tax[]
  const shouldApplyCompound =
    hasCompoundTaxFeatures(listingTaxes) && baseCalculation.subTotal > 0

  // STEP 3: Return early if no compound taxes needed
  if (!shouldApplyCompound) {
    return baseCalculation
  }

  // STEP 4: Apply compound taxes to price tiers
  const compoundResult = applyCompoundTaxesToAmount(
    baseCalculation.totalPriceTiersAmount,
    listingTaxes,
  )

  // STEP 5: Calculate add-ons with compound taxes
  const totalAddOnsAmountBeforeDiscount = (cartItem?.addOns || []).reduce(
    (sum, addOn) => sum + addOn.price * addOn.selectedNumber,
    0,
  )

  // Calculate the discount portion for addons from baseCalculation
  const fixedAddOnsDiscountAmount = baseCalculation.addOnsDiscountAmount

  const addOnResults = (cartItem?.addOns || []).reduce(
    (acc, addOn) => {
      if (!addOn.selectedNumber) return acc

      const addOnTaxes = (addOn.taxes || []).map(tax => ({
        ...tax,
        amount: 'amount' in tax ? tax.amount * addOn.selectedNumber : undefined,
      })) as Tax[]

      const addOnTotalPrice = addOn.price * addOn.selectedNumber

      // Calculate this addon's portion of the discount
      const addOnFixedDiscountPortion =
        totalAddOnsAmountBeforeDiscount > 0
          ? round(
              (fixedAddOnsDiscountAmount * addOnTotalPrice) /
                totalAddOnsAmountBeforeDiscount,
            )
          : 0

      // Calculate final discounted amount
      const addOnDiscountedAmount = Math.max(
        0,
        round(addOnTotalPrice - addOnFixedDiscountPortion),
      )

      // Check if this add-on has compound tax features
      if (hasCompoundTaxFeatures(addOnTaxes) && addOnDiscountedAmount > 0) {
        const addOnCompoundResult = applyCompoundTaxesToAmount(
          addOnDiscountedAmount,
          addOnTaxes,
        )

        return {
          hasCompoundTaxes: true,
          includedTaxes: [
            ...acc.includedTaxes,
            ...addOnCompoundResult.includedTaxes,
          ],
          nonIncludedTaxes: [
            ...acc.nonIncludedTaxes,
            ...addOnCompoundResult.nonIncludedTaxes,
          ],
          totalAmount: acc.totalAmount + addOnCompoundResult.totalAmount,
        }
      }

      // No compound taxes, use simple calculation (already handles discounts correctly)
      return {
        ...acc,
        totalAmount: acc.totalAmount + addOnDiscountedAmount,
      }
    },
    {
      hasCompoundTaxes: false,
      includedTaxes: [] as IncludedTax[],
      nonIncludedTaxes: [] as NonIncludedTax[],
      totalAmount: 0,
    },
  )

  // STEP 6: Determine which add-on taxes to use
  const finalAddOnIncludedTaxes = addOnResults.hasCompoundTaxes
    ? addOnResults.includedTaxes
    : baseCalculation.includedAddOnsTaxes

  const finalAddOnNonIncludedTaxes = addOnResults.hasCompoundTaxes
    ? addOnResults.nonIncludedTaxes
    : baseCalculation.nonIncludedAddOnsTaxes

  const finalAddOnTotal = addOnResults.hasCompoundTaxes
    ? addOnResults.totalAmount
    : baseCalculation.totalAddOnsAmount +
      baseCalculation.nonIncludedAddOnsTaxes.reduce(
        (sum, tax) => sum + tax.amountFee,
        0,
      )

  // STEP 7: Merge all included taxes
  const allIncludedTaxes = [
    ...compoundResult.includedTaxes,
    ...finalAddOnIncludedTaxes,
  ]

  // STEP 8: Merge and deduplicate non-included taxes
  const taxMap = [
    ...compoundResult.nonIncludedTaxes,
    ...finalAddOnNonIncludedTaxes,
  ].reduce((map, tax) => {
    const existing = map.get(tax.name)
    return map.set(
      tax.name,
      existing
        ? { ...existing, amountFee: existing.amountFee + tax.amountFee }
        : { ...tax },
    )
  }, new Map<string, NonIncludedTax>())

  const allNonIncludedTaxes = Array.from(taxMap.values())

  return {
    ...baseCalculation,
    includedAddOnsTaxes: finalAddOnIncludedTaxes,
    includedPriceTiersTaxes: compoundResult.includedTaxes,
    includedTaxes: allIncludedTaxes,
    nonIncludedAddOnsTaxes: finalAddOnNonIncludedTaxes,
    nonIncludedPriceTiersTaxes: compoundResult.nonIncludedTaxes,
    nonIncludedTaxes: allNonIncludedTaxes,
    total: round(compoundResult.totalAmount + finalAddOnTotal),
  }
}
